<template>
  <div v-if="show" v-rect-loading="loading" class="ai-requirement-content">
    <div class="header-container">
      <div class="title">{{ $t(`${i18nPrefix}.label.aiJobRequirementCriterionTipFirst`) }}</div>
      <div class="title">{{ $t(`${i18nPrefix}.label.aiJobRequirementCriterionTipSecond`) }}</div>
      <div class="title">{{ $t(`${i18nPrefix}.label.aiJobRequirementCriterionTipThird`) }}</div>
    </div>
    <div class="criterion-content">
      <div class="transfer-criterions">
        <div class="criterion-container suggestion">
          <div class="header">
            <span class="title">{{ $t(`${i18nPrefix}.label.suggestionAIJobRequirementCriterionTip`) }}</span>
            <i v-if="!disabled" class="refresh-icon iconfont iconrefresh1" @click="handleRefreshCriterion"></i>
          </div>
          <draggable
            v-model="suggestionCriterionList"
            animation="300"
            ghostClass="suggestion-ghost"
            class="criterion-list"
            :disabled="disabled"
            :options="suggestionDraggableOptions"
            :move="handleMoveSuggestionCriterion"
            @start="handleMoveStartSuggestionCriterion"
            @end="handleMoveEndSuggestionCriterion"
          >
            <transition-group class="transition-group">
              <div v-for="(criterion, index) in suggestionCriterionList" class="item-cls suggestion-item" :class="disabled && 'disabled'" :key="index" @click="handleClickCriterion(criterion)">
                <div class="suggestion-criterion-body" :class="[criterion.is_enabled && 'is-select']">
                  <span v-auto-tooltip class="criterion-label">{{ criterion.i18nName }}</span>
                </div>
                <i v-if="criterion.is_enabled" class="selected-icon el-icon-check"></i>
              </div>
            </transition-group>
          </draggable>
          <div v-if="suggestionCriterionList.length === 0" class="empty-text">{{ $t('common.label.noData') }}</div>
        </div>
        <div class="criterion-container selected">
          <div class="header">
            <span class="title">{{ $t(`${i18nPrefix}.label.selectAIJobRequirementCriterionTip`) }}</span>
            <i v-if="!disabled" class="add-icon iconfont iconAdd" @click="handleAddCriterionBtnClick"></i>
          </div>
          <draggable
            v-model="selectCriterionList"
            animation="300"
            ghostClass="selected-ghost"
            class="criterion-list"
            :disabled="disabled"
            :options="selectedDraggableOptions"
            :move="handleMoveSelectedCriterion"
            @sort="handleCriterionSort"
          >
            <transition-group class="transition-group">
              <div v-for="(criterion, index) in selectCriterionList" class="item-cls selected-item" :class="disabled && 'disabled'" :key="index">
                <div class="criterion-index">{{ index + 1 }}</div>
                <div class="criterion-info">
                  <div class="criterion">
                    <span v-auto-tooltip class="criterion-label">{{ criterion.i18nName }}</span>
                    <span v-if="!disabled" class="remove-btn" @click="handleRemoveCriterion(criterion.id)">
                      <i class="remove-icon el-icon-close"></i>
                    </span>
                  </div>
                </div>
                <i class="drag-btn iconfont iconMore" :class="disabled && 'disabled'"></i>
              </div>
              <div v-for="num in emptyCriterionList" class="item-cls selected-item" :class="disabled && 'disabled'" :key="`empty_${num}`">
                <div class="criterion-index is-empty">{{ selectCriterionList.length + num + 1 }}</div>
                <div class="criterion-info">
                  <div class="criterion empty-criterion"></div>
                </div>
              </div>
            </transition-group>
          </draggable>
        </div>
      </div>
    </div>
    <ConfirmDialog
      :show.sync="refreshCriterionDialog.show"
      :loading="refreshCriterionDialog.loading"
      :title="$t(`${i18nPrefix}.label.confirmRefreshCriterionTitle`)"
      :content="$t(`${i18nPrefix}.label.confirmRefreshCriterionContent`)"
      @cancel="handleCancelRefreshCriterion"
      @confirm="handleConfirmRefreshCriterion"
    ></ConfirmDialog>
    <DialogWrapper
      :show.sync="showAddCriterionDialog"
      :title="$t(`${i18nPrefix}.label.addAICriterionTip`)"
      @save="handleAddCriterion"
      @closed="handleCancelAddCriterion"
    >
      <WrappedForm
        ref="jobRequirementCriterionForm"
        class="add-job-requirement-criterion"
        label-width="200px"
        :model="needAddCriterionInfo"
        :formLines="formLines"
      ></WrappedForm>
    </DialogWrapper>
  </div>
</template>

<script>
import _ from 'lodash'
import { addI18nProperty, getI18nPropertyValue } from '@/i18n'
import mixin from './mixin'
import { mapActions } from 'vuex'
import WrappedForm from '@/components/formEditor/wrappedForm.vue'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import ConfirmDialog from '@/components/dialog/confirmDialog.vue'
import draggable from 'vuedraggable'

const MAX_CRITERION_COUNT = 10

export default {
  mixins: [mixin],
  components: {
    WrappedForm,
    DialogWrapper,
    ConfirmDialog,
    draggable
  },
  data () {
    return {
      MAX_CRITERION_COUNT,
      hasForm: false,
      loading: false,
      needAddCriterionInfo: {},
      needAddCriterionName: null,
      selectCriterionList: [],
      suggestionCriterionList: [],
      originSuggestionCriterionList: [],
      suggestionDraggableOptions: {
        group: {
          name: 'site',
          pull: 'clone'
        },
        sort: false
      },
      selectedDraggableOptions: {
        group: {
          name: 'site',
          pull: false,
          put: true
        },
        sort: true
      },
      existsCriterionNameList: [],
      addCriterionList: [],
      refreshCriterionDialog: {
        show: false,
        loading: false
      },
      showAddCriterionDialog: false,
      ghostCriterionCount: 0
    }
  },
  computed: {
    formLines () {
      const lines = {
        name: {
          label: this.$t(`${this.i18nPrefix}.label.jobRequirementCriterionName`),
          rules: [{ required: true, message: this.$t('common.message.requiredFormField'), trigger: 'blur' }, { validator: this.validateCriterion, trigger: 'blur' }]
        }
      }
      return lines
    },
    // 与已经选中的criterion 列表对比，最大值 MAX_CRITERION_COUNT 个
    emptyCriterionList () {
      return _.range(0, _.max([MAX_CRITERION_COUNT - this.selectCriterionList.length - this.ghostCriterionCount, 0]))
    }
  },
  created () {
    this.initPage()
  },
  methods: {
    ...mapActions('job', ['getJobCriterionList', 'calculateJobCriterion']),
    initPage () {
      this.loadJobCriterion()
    },
    normalizeCriterionList (criterionList) {
      _.forEach(criterionList, item => {
        addI18nProperty(item, 'i18nName', 'name_cn', 'name_en')
      })
      return criterionList
    },
    async loadJobCriterion () {
      if (!this.jobInfo.id) {
        return
      }
      this.loading = true
      try {
        const resp = await this.getJobCriterionList({ job_id: this.jobInfo.id, is_deleted: 0 })
        if (resp) {
          this.suggestionCriterionList = this.normalizeCriterionList(resp)
          this.selectCriterionList = _.chain(this.suggestionCriterionList).filter(criterion => criterion.is_enabled).orderBy('order', 'asc').value()
          this.originSuggestionCriterionList = _.cloneDeep(resp)
          this.resetExistsCriterionNames()
        }
      } finally {
        this.loading = false
      }
    },
    handleMoveSuggestionCriterion (e) {
      const criterion = e.draggedContext.element
      if (criterion.is_enabled) {
        return false
      }
      if (this.selectCriterionList.length >= 10) {
        return false
      }
      // 拖动到选中列表
      if (e.from !== e.to) {
        this.ghostCriterionCount = 1
      }
      return true
    },
    validateSelectedCriterion () {
      if (this.selectCriterionList.length >= 10) {
        this.$message({
          type: 'warning',
          message: this.$t(`${this.i18nPrefix}.message.criterionMaxLimit`, { num: MAX_CRITERION_COUNT })
        })
        return false
      }
      return true
    },
    handleMoveStartSuggestionCriterion (e) {
      this.validateSelectedCriterion()
    },
    handleMoveEndSuggestionCriterion () {
      this.ghostCriterionCount = 0
      _.forEach(this.selectCriterionList, (criterion, index) => {
        criterion.is_enabled = true
        criterion.order = index
      })
    },
    handleClickCriterion (criterion) {
      if (this.disabled || (!criterion.is_enabled && !this.validateSelectedCriterion())) {
        return
      }
      criterion.is_enabled = !criterion.is_enabled
      this.selectCriterionList = _.chain(this.suggestionCriterionList).filter(criterion => criterion.is_enabled).orderBy('order', 'asc').value()
    },
    handleRemoveCriterion (criterionId) {
      const criterion = _.find(this.suggestionCriterionList, criterion => criterion.id === criterionId)
      if (criterion) {
        criterion.is_enabled = false
      }
      // 将 criterion 从 selectCriterionList 中移除
      this.selectCriterionList = _.filter(this.selectCriterionList, criterion => criterion.id !== criterionId)
      this.resetSelectedCriterionOrder()
    },
    handleMoveSelectedCriterion (e) {
      const criterion = e.draggedContext.element
      return Boolean(criterion)
    },
    handleCriterionSort (e) {
      // 拖动排序结束
      if (e.type === 'sort') {
        const oldIndex = e.oldIndex
        const newIndex = e.newIndex
        if (oldIndex !== newIndex) {
          const oldCriterion = this.selectCriterionList[oldIndex]
          if (oldCriterion) {
            oldCriterion.order = newIndex
          }
          const newCriterion = this.selectCriterionList[newIndex]
          if (newCriterion) {
            newCriterion.order = oldIndex
          }
          // 将 selectCriterionList 中所有元素 order 重新计算
          this.resetSelectedCriterionOrder()
        }
      }
    },
    resetExistsCriterionNames () {
      this.existsCriterionNameList = []
      _.forEach(this.suggestionCriterionList, criterion => {
        this.existsCriterionNameList.push(criterion.name_cn)
        this.existsCriterionNameList.push(criterion.name_en)
      })
    },
    resetSelectedCriterionOrder () {
      _.forEach(this.selectCriterionList, (criterion, index) => {
        criterion.order = index
      })
    },
    validateCriterion (rule, value, callback) {
      if (_.includes(this.existsCriterionNameList, value)) {
        callback(new Error(this.$t(`${this.i18nPrefix}.message.requirementCriterionKeyHasExists`)))
      } else {
        callback()
      }
    },
    handleRefreshCriterion () {
      this.refreshCriterionDialog.show = true
    },
    handleCancelRefreshCriterion () {
      this.refreshCriterionDialog.show = false
    },
    mergeArrays (oldList, noChangeList, newList) {
      // 创建映射以便快速查找
      const mapOldList = new Map()
      _.forEach(oldList, item => {
        const key = JSON.stringify([item.name_cn, item.name_en])
        mapOldList.set(key, _.cloneDeep(item)) // 使用深拷贝避免原对象被修改
      })

      // 更新 noChangeList 和 newList 中的对象，使其字段值与 oldList 中一致
      function updateArray (arr) {
        return _.map(arr, item => {
          const key = JSON.stringify([item.name_cn, item.name_en])
          return mapOldList.has(key) ? mapOldList.get(key) : item
        })
      }

      const updatedChangeList = updateArray(noChangeList)
      const updatedNewList = updateArray(newList)

      // 合并数组B和C，同时去重
      const combinedList = _.unionBy(updatedChangeList, updatedNewList, item =>
        JSON.stringify([item.name_cn, item.name_en])
      )

      return combinedList
    },
    async handleConfirmRefreshCriterion () {
      this.refreshCriterionDialog.loading = true
      try {
        const params = {
          job_id: this.jobInfo.id
        }
        const resp = await this.calculateJobCriterion(params)
        if (resp) {
          this.suggestionCriterionList = this.mergeArrays(this.suggestionCriterionList, this.addCriterionList, resp)
          this.normalizeCriterionList(this.suggestionCriterionList)
          this.resetExistsCriterionNames()
        }
      } finally {
        this.refreshCriterionDialog.loading = false
        this.refreshCriterionDialog.show = false
      }
    },
    handleAddCriterionBtnClick () {
      if (!this.validateSelectedCriterion()) {
        return
      }
      this.showAddCriterionDialog = true
    },
    handleCancelAddCriterion () {
      this.showAddCriterionDialog = false
      this.needAddCriterionInfo = {}
    },
    async handleAddCriterion () {
      const isValidate = await this.$refs.jobRequirementCriterionForm.submit()
      if (!isValidate) {
        return
      }
      const criterion = {
        name: this.needAddCriterionInfo.name,
        name_en: this.needAddCriterionInfo.name,
        name_cn: this.needAddCriterionInfo.name,
        id: `HR_MANUAL_${this.suggestionCriterionList.length + 1}`,
        is_enabled: true,
        order: this.selectCriterionList.length,
        get i18nName () {
          return getI18nPropertyValue(this, 'name_cn', 'name_en')
        }
      }

      this.selectCriterionList.push(criterion)
      this.addCriterionList.push(criterion)
      this.existsCriterionNameList.push(criterion.name)
      this.handleCancelAddCriterion()
    },
    getAllCriterionList () {
      // 将在 suggestionCriterionList 中 is_enabled = false 的 criterion 和 selectCriterionList 合并
      const allCriterionList = _.concat(this.selectCriterionList, _.filter(this.suggestionCriterionList, criterion => !criterion.is_enabled))
      return allCriterionList
    },
    resetCriterionList () {
      this.suggestionCriterionList = this.normalizeCriterionList(_.cloneDeep(this.originSuggestionCriterionList))
      this.selectCriterionList = _.chain(this.suggestionCriterionList).filter(criterion => criterion.is_enabled).orderBy('order', 'asc').value()
      this.resetExistsCriterionNames()
    }
  },
  watch: {
    show () {
      if (!this.jobInfo.id) {
        if (this.jobInfo.job_criterion_list?.length) {
          this.suggestionCriterionList = this.normalizeCriterionList(this.jobInfo.job_criterion_list)
          this.resetExistsCriterionNames()
        }
      } else {
        this.resetCriterionList()
      }
    },
    selectCriterionList () {
      this.hasChangedData = true
    }
  }
}
</script>

<style lang="scss" scoped>
.ai-requirement-content {
  .header-container {
    margin-bottom: 50px;
    .title {
      font-size: 16px;
      color: #606266;
      font-weight: 500;
      word-break: break-word;
      margin-bottom: 30px;
    }
  }
  .criterion-content {
    .transfer-criterions {
      display: flex;
      justify-content: space-between;
      min-height: 120px;

      .criterion-container {
        background-color: white;
        border: 1px solid #eee;
        .header {
          /* 列表标题在列表滚动时吸附在顶部 */
          position: sticky;
          top: 0px;
          z-index: 9;
          background-color: #F5F7FA;
          display: flex;
          align-items: center;
          height: 52px;
          justify-content: space-between;
          border-top-left-radius: 8px;
          border-top-right-radius: 8px;

          .title {
            font-size: 16px;
            color: #303133;
            font-weight: 500;
            margin-left: 32px;
          }

          .refresh-icon {
            color: #0096cc;
            font-size: 26px;
            margin-right: 48px;
            cursor: pointer;
          }

          .add-icon {
            color: #0096cc;
            font-size: 32px;
            margin-right: 28px;
            cursor: pointer;
          }
        }

        .criterion-list {
          max-height: 476px;
          overflow-y: scroll;
          padding: 14px 0 6px 0;
          .transition-group {
            min-height: 420px;
            display: block;

            .item-cls {
              display: flex;
              align-items: center;
              justify-content: space-between;
              overflow: hidden;
              white-space: nowrap;
              text-overflow: ellipsis;
              text-align: left;
              padding: 0 32px;
              font-size: 14px;
              color: #606266;
              cursor: pointer;

              &.disabled {
                cursor: not-allowed;
              }

              .label {

              }

              .criterion-index {
                width: 16px;
                color: #0096cc;
                font-size: 14px;
              }

              .criterion-info {
                flex: 1;
                display: flex;
                margin-left: 16px;
                .criterion {
                  padding: 4px 10px;
                  border: 1px solid #dddedf;
                  border-radius: 18px;
                  display: flex;
                  align-items: center;
                  margin-right: 12px;
                  max-width: 300px;

                  .criterion-label {
                    vertical-align: middle;
                  }

                  .remove-btn {
                    margin-left: 10px;
                    vertical-align: middle;
                    cursor: pointer;
                  }
                }
              }
              .drag-btn {
                font-size: 24px;
                &.disabled {
                  cursor: not-allowed;
                }
              }
              .suggestion-criterion-body {
                max-width: 266px;
                display: flex;
                flex: 1;
                font-size: 12px;
              }
              .selected-icon {
                color: #0096cc;
              }
              .is-select {
                color: #0096cc;
              }
            }

            .suggestion-item {
              line-height: 30px;
              &:hover {
                color: #0096cc;
                background-color: #F2FCFF;
              }

              &.disabled {
                &:hover {
                  color: #606266;
                  background-color: #fff;
                  cursor: not-allowed;
                }
              }
            }

            .selected-item {
              margin-bottom: 14px;
              .is-empty {
                color: #dddedf;
              }
              .criterion-info {
                .empty-criterion {
                  height: 30px;
                  width: 280px;
                  border: 1px dashed #dddedf;
                }
              }
            }
          }
        }
        /* 数据为空的样式 */
        .empty-text {
          text-align: center;
          color: #ccc;
        }
        /* 列表的滚动条样式重写 */
        &::-webkit-scrollbar {
          width: 1px;
        }
        &::-webkit-scrollbar-thumb {
          background: #ccc;
        }
        &::-webkit-scrollbar-track {
          background: #ededed;
        }
      }

      .suggestion {
        width: 44%;
        border-radius: 8px;
        max-height: 372px;
        .criterion-list {
          max-height: 310px;
          .transition-group {
            .suggestion-ghost {
            }
          }
        }
      }

      .selected {
        margin-left: 52px;
        width: 56%;
        border-radius: 8px;
        .criterion-list {
          padding-top: 20px;
          .transition-group {
            .selected-ghost {
              padding: 0 32px;
              .criterion-info {
                flex: 1;
                display: flex;
                margin-left: 20px;
                .criterion {
                  display: flex;
                  align-items: center;
                  justify-content: space-between;
                  padding: 4px 10px;
                  height: 30px;
                  width: 330px;
                  border: 1px dashed #dddedf;
                  border-radius: 18px;
                }
              }
              .suggestion-criterion-body {
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 4px 10px;
                height: 30px;
                width: 330px;
                border: 1px dashed #dddedf;
                border-radius: 18px;
                margin-bottom: 14px;
              }
            }
          }
        }

      }
    }
  }
}
</style>
